import { usePaginatedQuery, useUrlPaginatedQuery } from "@shared/hooks";
import type { PaginatedQueryParams } from "@shared/api";
import { axiosGetData } from "@shared/api/clients";
import { useQuery } from "@tanstack/react-query";

import type {
  ComplexFilters,
  ListComplexesResponse,
  GetComplexByIdResponse,
} from "./complexes.types";

const COMPLEXES_QUERY_KEY = "complexes";

const getComplexesEndpoint = (tenantId: string) =>
  `v1/managerial/tenants/${tenantId}/complexes`;

export const COMPLEXES_FILTER_KEYS: (keyof ComplexFilters)[] = [
  "name",
  "fantasyName",
  "cnpj",
  "responsibleName",
  "sortBy",
  "sortOrder",
];

export const getComplexById = async (
  tenantId: string,
  complexId: string,
): Promise<GetComplexByIdResponse> => {
  return axiosGetData<GetComplexByIdResponse>(
    `${getComplexesEndpoint(tenantId)}/${complexId}`,
  );
};

export const useGetComplexByIdQuery = (
  tenantId: string,
  complexId: string,
  options = {},
) => {
  return useQuery({
    queryKey: [COMPLEXES_QUERY_KEY, tenantId, complexId],
    queryFn: () => getComplexById(tenantId, complexId),
    enabled: !!tenantId && !!complexId,
    ...options,
  });
};

export const useListComplexesQuery = (
  tenantId: string,
  params?: PaginatedQueryParams<ComplexFilters>,
) => {
  return usePaginatedQuery<ListComplexesResponse, ComplexFilters>({
    endpoint: getComplexesEndpoint(tenantId),
    queryKeyBase: [COMPLEXES_QUERY_KEY, tenantId],
    params,
    enabled: !!tenantId,
  });
};

export const useListComplexesUrlQuery = (tenantId: string | null) => {
  return useUrlPaginatedQuery<ListComplexesResponse, ComplexFilters>({
    endpoint: getComplexesEndpoint(tenantId ?? ""),
    queryKeyBase: [COMPLEXES_QUERY_KEY, tenantId ?? ""],
    filterKeys: COMPLEXES_FILTER_KEYS,
    enabled: !!tenantId,
  });
};
